import React, { useRef } from 'react';
import { JcButton, JcButtonType } from './button/JcButton';
import { JcCommonProps, JcUtils } from '../utils/JcUtils';
import { useJcFieldId } from '../utils/useJcFieldId';

interface JcFileInputBaseProps extends JcCommonProps {
  /** Native `accept` filter, e.g. `"image/*"`. */
  readonly accept?: string;
  readonly disabled?: boolean;
  readonly buttonType?: JcButtonType;
  readonly children: React.ReactNode;
}

interface JcSingleFileInputProps extends JcFileInputBaseProps {
  readonly multiple?: false;
  /** Called with the chosen file. Re-selecting the same file fires again. */
  readonly onFileSelected: (file: File) => void;
}

interface JcMultipleFileInputProps extends JcFileInputBaseProps {
  readonly multiple: true;
  /** Called with every chosen file, in picker order. */
  readonly onFilesSelected: (files: File[]) => void;
}

export type JcFileInputProps = JcSingleFileInputProps | JcMultipleFileInputProps;

/**
 * A button that opens the native file picker, hiding the `<input type="file">`
 * behind it. Yields the chosen `File` (or `File[]` with `multiple`); the caller
 * owns upload and progress.
 */
export const JcFileInput: React.FunctionComponent<JcFileInputProps> = (props: JcFileInputProps) => {
  // The id belongs on the file input itself, so keep it off the visible button.
  const { accept, disabled, buttonType, children, id } = props;
  const commonProps = JcUtils.commonProps({ ...props, id: undefined });
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useJcFieldId(id);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files ?? []);
    if (props.multiple) {
      if (files.length > 0) {
        props.onFilesSelected(files);
      }
    } else if (files[0]) {
      props.onFileSelected(files[0]);
    }
    // Clear so selecting the same file again still fires onChange.
    event.target.value = '';
  };

  return (
    <>
      <JcButton
        {...commonProps}
        buttonType={buttonType}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {children}
      </JcButton>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        multiple={props.multiple}
        disabled={disabled}
        style={{ display: 'none' }}
        onChange={handleChange}
      />
    </>
  );
};
